/**
 * SiiStatusBanner — aviso de que el SII está demorando la validación.
 *
 * Nace del 2026-10-28: el SII dejó facturas de varias empresas en «Schema
 * Validado» por más de una hora y los clientes veían «Consultando» sin saber si
 * era Clobi. Volver a emitir gasta folios, así que el aviso lo dice explícito.
 *
 * Quién lo ve lo decide el backend (`/config/sii-status/`): en automático, solo
 * los tenants de Chile con envíos esperando al SII; forzado por el platform
 * admin, todos los de Chile. Desaparece solo cuando la vigilancia ve que el SII
 * se normalizó.
 *
 * Se puede cerrar por la sesión: el usuario ya lo leyó y la tabla sigue
 * mostrando «Consultando». Si el aviso cambia (otro episodio, o pasa de
 * automático a manual) vuelve a aparecer, porque la clave es su inicio.
 */
import { useState } from 'react'
import { Hourglass, X } from 'lucide-react'
import { useSiiNotice } from '@/modules/platform/hooks/usePlatform'

const CLAVE = 'clobi:aviso-sii-cerrado'

function leerCerrado(): string | null {
  try {
    return sessionStorage.getItem(CLAVE)
  } catch {
    return null
  }
}

export function SiiStatusBanner() {
  const { data } = useSiiNotice()
  const [cerrado, setCerrado] = useState<string | null>(leerCerrado)

  const aviso = data?.notice ?? null
  if (!aviso) return null
  const clave = `${aviso.source}:${aviso.since ?? ''}`
  if (cerrado === clave) return null

  const cerrar = () => {
    try {
      sessionStorage.setItem(CLAVE, clave)
    } catch {
      // Sin almacenamiento igual se cierra hasta recargar.
    }
    setCerrado(clave)
  }

  return (
    <div
      role="status"
      aria-live="polite"
      className="shrink-0 relative flex items-center justify-center gap-2.5 px-10 py-2 text-sm bg-warning-solid text-amber-950"
    >
      <Hourglass size={16} className="flex-shrink-0" aria-hidden="true" />
      <p className="text-center leading-snug">
        <span className="font-semibold">Demora en el SII</span>
        <span className="hidden sm:inline"> — </span>
        {' '}
        <span className="opacity-95">{aviso.message}</span>
      </p>
      <button
        onClick={cerrar}
        aria-label="Cerrar aviso"
        title="Cerrar — vuelve a aparecer si cambia el aviso"
        className="absolute right-3 p-1.5 rounded-full hover:bg-black/20 transition-colors"
      >
        <X size={14} aria-hidden="true" />
      </button>
    </div>
  )
}
